import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus,faAddressBook } from '@fortawesome/free-solid-svg-icons';
import { getToken } from '../../function/token';
import domain from '../../../reusecomponent/domain';
import { ErrorToastContainer, SuccessToastContainer } from '../../function/CustomFunction';
import Preloader from '../preloader';
import CreateContactModal from './CreateContactModal';
import Swal from 'sweetalert2';
import './contacts.css';

export default function ContactsTable() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingContact, setEditingContact] = useState(null);

  const [modalForm, setModalForm] = useState({
    name: '',
    number: '',
    email: '',
    profilePicture: null,
    profilePictureUrl: null,
    address: '',
    city: '',
    district: '',
    state: '',
    pin: ''
  });

  const getInitials = (fullName) => {
    if (!fullName || typeof fullName !== 'string') return '??';
    const parts = fullName.trim().split(/\s+/);
    const initials = parts.slice(0, 2).map(p => p[0]?.toUpperCase()).join('');
    return initials || '??';
  };

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const headers = new Headers({
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      });
      const response = await fetch(`${domain()}/contact`, {
        method: 'GET',
        headers,
      });
      const result = await response.json();
      if (result.status && Array.isArray(result.data)) {
        setContacts(result.data);
      } else if (result.data) {
        // If data is base64 encoded
        try {
          const decoded = JSON.parse(atob(result.data));
          setContacts(decoded);
        } catch {
          setContacts([]);
        }
      } else {
        setContacts([]);
      }
    } catch (error) {
      setContacts([]);
      ErrorToastContainer('Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const handleDeleteContact = async (contactId, number) => {
    // if (!hasPermission(PERMISSIONS.USERS_CONTACTS_DELETE)) {
    //   ErrorToastContainer("You don't have permission to delete contacts");
    //   return;
    // }
    
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        const token = getToken();
        const response = await fetch(`${domain()}/contact/${contactId}/${btoa(number)}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const result = await response.json();
        if (result.status) {
          SuccessToastContainer('Contact deleted successfully!');
          fetchContacts();
        } else {
          ErrorToastContainer(result.message || 'Failed to delete contact');
        }
      } catch (error) {
        console.error('Error deleting contact:', error);
        ErrorToastContainer('Network error. Please try again.');
      }
    }
  };

  const handleAddContact = () => {
    // if (!hasPermission(PERMISSIONS.USERS_CONTACTS_CREATE)) {
    //   ErrorToastContainer("You don't have permission to create contacts");
    //   return;
    // }
    
    setEditingContact(null);
    setModalForm({
      name: '', number: '', email: '', profilePicture: null, profilePictureUrl: null,
      address: '', city: '', district: '', state: '', pin: ''
    });
    setShowModal(true);
  };

  const handleEditContact = (contact) => {    
    setEditingContact(contact);
    setModalForm({
      name: contact.name || '',
      number: contact.number || contact.mobile || '',
      email: contact.email || '',
      profilePicture: null,
      profilePictureUrl: contact.profile_picture || null,
      address: contact.address || '',
      city: contact.city || '',
      district: contact.district || '',
      state: contact.state || '',
      pin: contact.pin || ''
    });
    setShowModal(true);
  };

  const handleModalClose = () => {
    if (modalForm.profilePictureUrl && modalForm.profilePictureUrl.startsWith('blob:')) {
      URL.revokeObjectURL(modalForm.profilePictureUrl);
    }
    setShowModal(false);
    setEditingContact(null);
  };

  const filteredContacts = contacts.filter(contact =>
    contact.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.number?.includes(searchTerm) ||
    contact.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.state?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <Preloader action={true} />;
  }


  return (
    <div className="contacts-container">
      <div className="contacts-header">
        <div className="contacts-title-section">
          <h1 className="contacts-title">
            <FontAwesomeIcon icon={faAddressBook} className="contacts-title-icon" />
            Manage Contacts
          </h1>
          <p className="contacts-subtitle">Create, edit, and manage your contact list</p>
        </div>
        <button 
          className="btn btn-primary contacts-add-btn"
          onClick={handleAddContact}
        >
          <FontAwesomeIcon icon={faPlus} className="contacts-add-icon" />
          <span className="contacts-add-text">Add New Contact</span>
        </button>
      </div>

      <Preloader action={loading} />

      {!loading && (
        <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', background: '#fff' }}>
          {/* Search Bar */}
          <div className="contacts-search-section mb-4">
            <div className="search-container">
              <input
                type="text"
                className="search-input form-control"
                placeholder="Search contacts by name, email, number, city, or state..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ borderRadius: '8px', border: '1px solid #cbd5e1', padding: '10px 14px' }}
              />
            </div>
          </div>

          {filteredContacts.length > 0 ? (
            <div className="contacts-table-container border-0 shadow-none p-0">
              {/* Desktop Table View */}
              <div className="contacts-table-desktop">
                <div className="table-responsive" style={{ borderRadius: '12px' }}>
                <table className="table payment-history-table align-middle mb-0">
                  <thead>
                    <tr>
                      <th className="table-header">Profile</th>
                      <th className="table-header">Name</th>
                      <th className="table-header">Number</th>
                      <th className="table-header">Email</th>
                      <th className="table-header">Address</th>
                      <th className="table-header">City</th>
                      <th className="table-header">District</th>
                      <th className="table-header">State</th>
                      <th className="table-header">PIN</th>
                      <th className="table-header actions-header">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredContacts.map((contact) => (
                      <tr key={contact.id || contact._id} className="table-row">
                        <td className="profile-cell">
                          <span className="name-avatar">
                            {contact.profile_picture ? (
                              <img
                                src={`https://api.campaigningtool.com/contact_picture/${contact.profile_picture}`}
                                alt={contact.name || 'Profile'}
                                className="name-avatar-img"
                                onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }}
                              />
                            ) : null}
                            <span className="name-avatar-fallback" style={{ display: contact.profile_picture ? 'none' : 'flex' }}>{getInitials(contact.name)}</span>
                          </span>
                        </td>
                        <td className="name-cell">
                          <span className="contact-name">{contact.name}</span>
                        </td>
                        <td className="phone-cell">
                          <span className="contact-phone">{contact.number}</span>
                        </td>
                        <td className="email-cell">
                          <span className="contact-email">{contact.email}</span>
                        </td>
                        <td className="address-cell">
                          <span className="contact-address">{contact.address}</span>
                        </td>
                        <td className="city-cell">
                          <span className="contact-city">{contact.city}</span>
                        </td>
                        <td className="district-cell">
                          <span className="contact-district">{contact.district}</span>
                        </td>
                        <td className="state-cell">
                          <span className="contact-state">{contact.state}</span>
                        </td>
                        <td className="pin-cell">
                          <span className="contact-pin">{contact.pin}</span>
                        </td>
                        <td className="actions-cell">
                          <div className="actions-wrapper">
                            {/* <PermissionGuard permission={PERMISSIONS.USERS_CONTACTS_UPDATE}>
                              <button
                                className="action-btn action-edit"
                                onClick={() => handleEditContact(contact)}
                                title="Edit Contact"
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </button>
                            </PermissionGuard>
                            
                            <PermissionGuard permission={PERMISSIONS.USERS_CONTACTS_DELETE}>
                              <button
                                className="action-btn action-delete"
                                onClick={() => handleDeleteContact(btoa(contact.id), contact.number)}
                                title="Delete Contact"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </PermissionGuard> */}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>

              {/* Mobile Card View */}
              <div className="contacts-table-mobile">
                {filteredContacts.map((contact) => (
                  <div key={contact.id || contact._id} className="contact-card">
                    <div className="contact-card-header">
                      <div className="contact-info" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {contact.profile_picture ? (
                          <img
                            src={`https://api.campaigningtool.com/web/whatsapp/contact_picture/${contact.profile_picture}`}
                            alt={contact.name || 'Profile'}
                            style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                            onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }}
                          />
                        ) : null}
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            background: '#e9ecef',
                            color: '#495057',
                            display: contact.profile_picture ? 'none' : 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 600
                          }}
                        >
                          {getInitials(contact.name)}
                        </div>
                        <h3 className="contact-card-name">{contact.name}</h3>
                        <span className="contact-card-number">{contact.number}</span>
                      </div>
                      <div className="contact-card-actions">
                        {/* <PermissionGuard permission={PERMISSIONS.USERS_CONTACTS_UPDATE}>
                          <button
                            className="mobile-action-btn mobile-edit-btn"
                            onClick={() => handleEditContact(contact)}
                            title="Edit Contact"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                        </PermissionGuard>
                        <PermissionGuard permission={PERMISSIONS.USERS_CONTACTS_DELETE}>
                          <button
                            className="mobile-action-btn mobile-delete-btn"
                            onClick={() => handleDeleteContact(btoa(contact.id), contact.number)}
                            title="Delete Contact"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </PermissionGuard> */}
                      </div>
                    </div>
                    
                    <div className="contact-card-body">
                      <div className="contact-detail-row">
                        <span className="detail-label">Email:</span>
                        <span className="detail-value">{contact.email}</span>
                      </div>
                      <div className="contact-detail-row">
                        <span className="detail-label">Address:</span>
                        <span className="detail-value">{contact.address}</span>
                      </div>
                      <div className="contact-detail-row">
                        <span className="detail-label">City:</span>
                        <span className="detail-value">{contact.city}</span>
                      </div>
                      <div className="contact-detail-row">
                        <span className="detail-label">District:</span>
                        <span className="detail-value">{contact.district}</span>
                      </div>
                      <div className="contact-detail-row">
                        <span className="detail-label">State:</span>
                        <span className="detail-value">{contact.state}</span>
                      </div>
                      <div className="contact-detail-row">
                        <span className="detail-label">PIN:</span>
                        <span className="detail-value">{contact.pin}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="contacts-empty-state">
              <div className="empty-state-icon">
                <FontAwesomeIcon icon={faAddressBook} />
              </div>
              <h2 className="empty-state-title">No contacts found</h2>
              <p className="empty-state-description">
                {searchTerm ? 'Try adjusting your search terms.' : 'Get started by adding your first contact.'}
              </p>
              {/* {!searchTerm && hasPermission(PERMISSIONS.USERS_CONTACTS_CREATE) && (
                <button 
                  className="btn btn-primary empty-state-btn"
                  onClick={handleAddContact}
                >
                  <FontAwesomeIcon icon={faPlus} className="empty-state-btn-icon" />
                  Add First Contact
                </button>
              )} */}
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Contact Modal */}
      <CreateContactModal
        showModal={showModal}
        onClose={handleModalClose}
        editMode={!!editingContact}
        editData={editingContact}
      />
    </div>
  );
}